import React, { useEffect, useState, useRef } from 'react';
import {
  ScheduleComponent,
  TreeViewArgs,
  ResourcesDirective,
  ResourceDirective,
  ViewsDirective,
  ViewDirective,
  ResourceDetails,
  Inject,
  TimelineViews,
  Resize,
  // DragAndDrop,
  TimelineMonth
  // Day
} from '@syncfusion/ej2-react-schedule';
import moment from 'moment';
import { UnitTypeUiModel } from '../../../../../shared/ui-models/unit-type.ui-model';
import { PUBLIC_PATH, APP_ROUTES } from '../../../../../shared/config/routes-config';
import flattenDeep from 'lodash/flattenDeep';
import { RouteConfig } from '../../../../../shared/interfaces/routes-config.interface';
import { BlockedDatesUiModel } from '../../../../../shared/ui-models/blocked-dates.ui-model';
import { getAllReservations } from '../../../../../ReservationModule/shared/services/data.service';
import { isErrorInfo, ErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { getAllBlockingDates } from '../../../../../BlockDatesModule/shared/services/data.service';
import { ReservationStatus } from '../../../../../shared/enums/reservation-status';
import { getAllUnitTypes } from '../../../../../shared/services/data/unit-type.data';
import { getModuleName } from '../../../../shared/services/dashboard.service';
import {
  ReservationListUiModel,
  ReservationUiModel
} from '../../../../../shared/ui-models/reservation.ui-model';
import { UnitTypeGroupUiModel } from '../../../../../shared/ui-models/unit-type-group.ui-model';
import {
  getAllAreas,
  getAllBuilding,
  getAllUnitTypeGroups
} from '../../../../../shared/services/data/lookups.data';
import uniqBy from 'lodash/uniqBy';
import { LoadingData } from '../../../../../ui/LoadingData';
import { birdToast } from '../../../../../shared/components/core-ui/toast';
import Tooltip from '../../../../../ui/Tooltip';
import {
  CALENDAR_COLORS,
  CalendarEvent,
  DEFAULT_PROPERTY_ID,
  SelectOption,
  UnitResource,
  UnitTypeInnerUnit
} from './helper';
import { getAllProperties } from '../../../../../PropertyModule/shared/services/data.service';
import { CalendarFiltersPanel, CalendarFilterValues } from './CalendarFiltersPanel';

export const MainCalendar: React.FC = () => {
  const [events, setEvents] = useState<CalendarEvent[]>();
  const [unitsData, setUnitsData] = useState<UnitResource[]>();
  const [sortedUnitsData, setSortedUnitsData] = useState<UnitResource[]>();

  const [uniqKey, setUniqKey] = useState(Date.now);
  const [allUnitTypes, setAllUnitTypes] = useState<SelectOption[]>([]);
  const [unitTypeGroupsList, setUnitTypeGroupsList] = useState<{ value: string; label: string }[]>(
    []
  );
  const [allProjects, setAllProjects] = useState<SelectOption[]>([]);

  const scheduleRef = useRef<ScheduleComponent | null>(null);
  const [areaOptions, setAreaOptions] = useState<SelectOption[]>([]);
  const [allUsers, setAllUsers] = useState<SelectOption[]>([]);
  const [allBuildingOptions, setAllBuildingOptions] = useState<
    { id?: string; name: string; projectId?: string }[]
  >([]);

  const [currentDataSelected, setCurrentDataSelected] = useState<Date>(new Date());
  const [lazyLoading, setLazyLoading] = useState<boolean>(false);

  const getReservations = (currentDate: Date, unitsForSort?: UnitResource[]) => {
    getAllReservations(
      {
        statusQuery: [
          ReservationStatus.CHECKED_IN,
          ReservationStatus.CHECKED_OUT,
          ReservationStatus.CONFIRMATION_PENDING,
          ReservationStatus.IN_HOUSE,
          ReservationStatus.CHECKED_IN_STARTED,
          ReservationStatus.PENDING_CHECK_IN
        ],
        $or: [
          {
            checkInDate: {
              $gte: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getTime(),
              $lte: moment(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0))
                .add(1, 'days')
                .toDate()
                .getTime()
            }
          },
          {
            checkOutDate: {
              $gte: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getTime(),
              $lte: moment(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0))
                .add(1, 'days')
                .toDate()
                .getTime()
            }
          },
          {
            $and: [
              {
                checkInDate: {
                  $lte: moment(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0))
                    .add(1, 'days')
                    .toDate()
                    .getTime()
                }
              },
              {
                checkOutDate: {
                  $gt: new Date(currentDate.getFullYear(), currentDate.getMonth(), +1, 0).getTime()
                }
              }
            ]
          }
        ],
        // areaId: area.value ? area.value : undefined
        unitTypeIds: unitsForSort?.map((u) => Number(u.unitTypeId))
      },
      1,
      0
    ).then((response: ReservationListUiModel | ErrorInfo) => {
      console.log('render data', response);
      if (!isErrorInfo(response)) {
        const reservations = response?.reservationList || [];

        getBlockingDates(
          { query: { isCancelled: false } },
          reservations,
          currentDate,
          unitsForSort
        );

        if (reservations.length === 0) {
          birdToast.info('No reservations found for this period');
        }
      } else {
        setLazyLoading(false);
        birdToast.error('Failed to load reservations');
      }
    });
  };
  const getBlockingDates = (
    query: {},
    reservations: ReservationUiModel[],
    currentDate?: Date,
    unitsForSort?: UnitResource[]
  ) => {
    getAllBlockingDates(query).then((response: BlockedDatesUiModel[] | ErrorInfo) => {
      setLazyLoading(false);

      if (!isErrorInfo(response)) {
        const blockingDates = response || [];
        const calendarEvents = [
          ...reservations.map((item, index) => ({
            Id: index,
            Subject:
              item.status === ReservationStatus.PENDING_CHECK_IN
                ? '[PENDING] ' + item.primaryGuestName
                : item.primaryGuestName,
            StartTime: moment(item.checkInDate).startOf('day').toDate(),
            EndTime: moment(item.checkOutDate).startOf('day').toDate(),
            IsAllDay: true,
            IsBlock: true,
            id: item.roomId,
            reservationId: item.id,
            blockedDate: false
          })),
          ...blockingDates.map((item, index) => ({
            Id: index,
            Subject: `BLOCKED (${item.description})`,
            StartTime: moment(item.fromDate).startOf('day').toDate(),
            EndTime: moment(item.toDate).startOf('day').toDate(),
            IsAllDay: true,
            IsBlock: true,
            id: item.roomId,
            blockedDateId: item.id,
            blockedDate: true
          }))
        ];

        setEvents(calendarEvents);

        sortUnitsByDuration(
          unitsForSort ?? unitsData,
          calendarEvents,
          currentDate ? currentDate : currentDataSelected
        );
      }
    });
  };

  const filterDataWithoutDuplicate = (data: UnitTypeUiModel[]) => {
    const rooms: SelectOption[] = [];
    const users: SelectOption[] = [];
    data?.forEach((unit) => {
      if (unit?.units?.length) {
        unit.units.forEach((room) => {
          rooms.push({
            value: room.unitId,
            label: room.shortName,
            propertyId: unit?.propertyId,
            userId: unit?.landlordId,
            areaId: unit.areaId,
            groupId: unit?.unitTypeGroupId
          });
        });
      }
      if (unit?.landlordId !== undefined && unit?.landlordUsername !== undefined) {
        users.push({
          value: unit?.landlordId,
          label: unit?.landlordUsername,
          roomId: unit?.units,
          areaId: unit?.areaId,
          propertyId: unit?.propertyId,
          groupId: unit?.unitTypeGroupId
        });
      }
    });

    if (rooms.length > 0) {
      setAllUnitTypes(uniqBy(rooms, 'value'));
    }

    if (users.length > 0) {
      setAllUsers(uniqBy(users, 'value'));
    }
  };

  const getUnitTypeName = (value: ResourceDetails | TreeViewArgs): string => {
    return (value as ResourceDetails).resourceData[(value as any).resource.textField] as string;
  };
  const getUnitTypeDescription = (value: ResourceDetails | TreeViewArgs): string => {
    return (value as ResourceDetails).resourceData.description as string;
  };
  // const getUnitTypeImage = (value: ResourceDetails): string => {
  //   return (value as ResourceDetails).resourceData.coverImage as string;
  // };

  const getAreaName = (value: ResourceDetails): string => {
    return (value as ResourceDetails).resourceData.area as string;
  };

  const flattenUnitTypes = (unitTypeList: UnitTypeUiModel[] | undefined): UnitResource[] =>
    flattenDeep(
      unitTypeList
        ? unitTypeList.map((unitType) =>
            unitType.units
              ? (unitType.units as UnitTypeInnerUnit[]).map((unit: UnitTypeInnerUnit) => ({
                  id: unit.unitId,
                  unitTypeId: unitType.id || '',
                  name: unit.shortName,
                  color: CALENDAR_COLORS[Math.floor(Math.random() * CALENDAR_COLORS.length)],
                  description: `${unitType.name} ${
                    unit.roomNumber && unit.roomNumber !== 'undefined' ? ' #' + unit.roomNumber : ''
                  }`,
                  coverImage: unitType.coverImage,
                  area: getAreaName({ resourceData: unitType } as unknown as ResourceDetails)
                }))
              : []
          )
        : []
    );

  function calculateMonthlyDuration(
    startTime: Date | string,
    endTime: Date | string,
    monthStartDate: Date,
    monthEndDate: Date
  ): number {
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);

    const effectiveStart = startDate < monthStartDate ? monthStartDate : startDate;
    const effectiveEnd = endDate > monthEndDate ? monthEndDate : endDate;

    const duration = Math.ceil(
      (effectiveEnd.getTime() - effectiveStart.getTime()) / (1000 * 3600 * 24)
    );
    return duration > 0 ? duration : 0;
  }

  const handleSearchClick = async (filters: CalendarFilterValues) => {
    birdToast.loading('Applying filters...');

    const normalizedSearch = filters.unitTypeSearch.value.trim().toLowerCase();
    const selectedProjectIds = filters.project.map((item) => item.value);
    const query: {
      isManaged: boolean;
      areaId?: string;
      propertyId?: string;
      propertyIds?: string[];
      landlordId?: string;
      unitTypeGroupId?: string;
      buildingId?: string;
    } = {
      isManaged: true
    };

    if (filters.area.value) {
      query.areaId = filters.area.value;
    }

    if (selectedProjectIds.length === 1) {
      query.propertyId = selectedProjectIds[0];
    } else if (selectedProjectIds.length > 1) {
      query.propertyIds = selectedProjectIds;
    }

    if (filters.user.value) {
      query.landlordId = filters.user.value;
    }

    if (filters.unitTypeGroup.value) {
      query.unitTypeGroupId = filters.unitTypeGroup.value;
    }

    if (filters.buildingSelect.value) {
      query.buildingId = filters.buildingSelect.value;
    }

    try {
      const unitTypesResponse = await getAllUnitTypes('DASHBOARD_MODULE', 0, -1, query);

      if (isErrorInfo(unitTypesResponse)) {
        birdToast.error('Failed to load filtered calendar data', 'Error');
        return;
      }

      const fetchedUnitTypes = unitTypesResponse.unitList;
      const tempUnitTypeList = fetchedUnitTypes.filter(
        (unit) =>
          (filters.unitType.value === '' ||
            unit?.units?.some((room) => room?.unitId?.includes(filters.unitType.value))) &&
          (normalizedSearch === '' ||
            unit.name?.toLowerCase().includes(normalizedSearch) ||
            unit.area?.toLowerCase().includes(normalizedSearch) ||
            unit.landlordUsername?.toLowerCase().includes(normalizedSearch) ||
            unit.propertyName?.toLowerCase().includes(normalizedSearch) ||
            unit?.units?.some(
              (room) =>
                (room.shortName && room.shortName.toLowerCase().includes(normalizedSearch)) ||
                (room.roomNumber && room.roomNumber.toLowerCase().includes(normalizedSearch)) ||
                (room.unitId && room.unitId.toLowerCase().includes(normalizedSearch))
            ))
      );

      filterDataWithoutDuplicate(fetchedUnitTypes);

      const filteredUnitsFlattened = flattenUnitTypes(tempUnitTypeList);

      setUnitsData(filteredUnitsFlattened);

      if (filteredUnitsFlattened.length === 0) {
        setEvents([]);
        setSortedUnitsData([]);
        setUniqKey(Date.now());
        birdToast.info('No units found for the selected filters', 'Calendar Updated');
        return;
      }

      setLazyLoading(true);
      getReservations(currentDataSelected, filteredUnitsFlattened);

      setTimeout(() => {
        if (scheduleRef && scheduleRef.current) {
          scheduleRef.current.refreshTemplates();
        }
      });
    } catch (error) {
      setLazyLoading(false);
      birdToast.error('Failed to load filtered calendar data', 'Error');
    }
  };

  const handleResetClick = async () => {
    birdToast.loading('Resetting filters...');

    try {
      const unitTypesResponse = await getAllUnitTypes('DASHBOARD_MODULE', 0, -1, {
        propertyId: DEFAULT_PROPERTY_ID,
        isManaged: true
      });

      if (isErrorInfo(unitTypesResponse)) {
        birdToast.error('Failed to reset calendar data', 'Error');
        return;
      }

      const defaultUnitTypes = unitTypesResponse.unitList;
      filterDataWithoutDuplicate(defaultUnitTypes);

      const filteredUnitsFlattened = flattenUnitTypes(defaultUnitTypes);
      setUnitsData(filteredUnitsFlattened);

      if (filteredUnitsFlattened.length === 0) {
        setEvents([]);
        setSortedUnitsData([]);
        setUniqKey(Date.now());
        birdToast.info('No default units found', 'Calendar Updated');
        return;
      }

      setLazyLoading(true);
      getReservations(currentDataSelected, filteredUnitsFlattened);
      birdToast.success('All filters have been reset');
    } catch (error) {
      setLazyLoading(false);
      birdToast.error('Failed to reset calendar data', 'Error');
    }
  };

  function sortUnitsByDuration(
    unitsData: UnitResource[] | undefined,
    events: CalendarEvent[] | undefined,
    currentDataSelected: Date
  ) {
    if (unitsData && events && currentDataSelected) {
      const selectedYear = currentDataSelected.getFullYear();
      const selectedMonth = currentDataSelected.getMonth();

      const monthStartDate = new Date(selectedYear, selectedMonth, 1);
      const monthEndDate = new Date(selectedYear, selectedMonth + 1, 0);

      const roomDurationMap: { [key: string]: number } = {};
      events.forEach((event) => {
        const duration = calculateMonthlyDuration(
          event.StartTime,
          event.EndTime,
          monthStartDate,
          monthEndDate
        );
        if (duration > 0) {
          // Only count if there is an overlap
          if (roomDurationMap[event.id]) {
            roomDurationMap[event.id] += duration;
          } else {
            roomDurationMap[event.id] = duration;
          }
        }
      });

      const sortedUnits = [...unitsData].sort((a, b) => {
        const durationA = roomDurationMap[a.id] || 0;
        const durationB = roomDurationMap[b.id] || 0;
        return durationA - durationB;
      });

      setSortedUnitsData(sortedUnits);
      setUniqKey(Date.now());
    }
  }

  useEffect(() => {
    getAllUnitTypes('DASHBOARD_MODULE', 0, -1, {
      propertyId: DEFAULT_PROPERTY_ID,
      isManaged: true
    })
      .then((unitTypesResponse) => {
        if (isErrorInfo(unitTypesResponse)) return;

        const UnitTypeList = unitTypesResponse.unitList;

        if (UnitTypeList.length > 0) {
          filterDataWithoutDuplicate(UnitTypeList);
        }

        const filteredUnitsFlattened: UnitResource[] = flattenUnitTypes(UnitTypeList);

        setUnitsData(filteredUnitsFlattened);

        // Fetch initial events only after units are available
        getReservations(new Date(), filteredUnitsFlattened);
      })
      .catch((error) => {
        console.error('[GlobalCalendar] Failed to load unit types/groups', error);
        birdToast.error('Unable to load calendar data. Please try again.');
      });

    getAllUnitTypeGroups(getModuleName()).then((response) => {
      if (!isErrorInfo(response)) {
        const groupsOptions = response.map((g: UnitTypeGroupUiModel) => ({
          value: g.id || '',
          label: g.name || ''
        }));
        setUnitTypeGroupsList(groupsOptions);
      }
    });

    getAllBuilding(getModuleName()).then((response) => {
      if (!isErrorInfo(response)) {
        setAllBuildingOptions(response);
      }
    });
    getAllAreas(getModuleName()).then((response) => {
      if (!isErrorInfo(response)) {
        const tempAllAreas = response?.map((area) => ({
          value: area.id || '',
          label: area.name || ''
        }));
        setAreaOptions(tempAllAreas);
      }
    });

    getAllProperties().then((response) => {
      if (!isErrorInfo(response)) {
        setAllProjects(
          response
            ?.filter((property) => property.areaId != null)
            .map((property) => ({
              value: property.id || '',
              label: property.name || '',
              areaId: property.areaId as string
            }))
        );
      }
    });
  }, []);

  return (
    <div className="flex flex-col justify-center">
      <CalendarFiltersPanel
        areaOptions={areaOptions}
        allProjects={allProjects}
        allUsers={allUsers}
        allUnitTypes={allUnitTypes}
        unitTypeGroupsList={unitTypeGroupsList}
        allBuildingOptions={allBuildingOptions}
        onSearch={handleSearchClick}
        onReset={handleResetClick}
      />
      {events && unitsData ? (
        <div className="schedule-control-section relative">
          <div className="col-lg-12 control-section">
            <div className="control-wrapper drag-sample-wrapper">
              <div className="schedule-container">
                {lazyLoading && (
                  <div className="absolute inset-0  bg-neutral-50/80 z-10 flex w-full h-full items-center justify-center py-10">
                    {/* <img src={`${PUBLIC_PATH}/assets/svgs/loader.svg`} alt="loader" /> */}
                    <LoadingData />
                  </div>
                )}
                <ScheduleComponent
                  key={uniqKey} // Changing key forces re-render
                  ref={scheduleRef}
                  navigating={(event) => {
                    setLazyLoading(true);
                    if (
                      event &&
                      event.currentDate &&
                      event.previousDate &&
                      event.previousDate !== event.currentDate
                    ) {
                      setCurrentDataSelected(event.currentDate);
                      birdToast.loading(
                        `Loading data for ${event.currentDate.toLocaleDateString('en-US', {
                          month: 'long',
                          year: 'numeric'
                        })}...`
                      );
                      getReservations(event.currentDate, sortedUnitsData ?? unitsData);
                    }
                  }}
                  // ref={(schedule) => (scheduleObj = schedule)}
                  cssClass="block-events"
                  width="100%"
                  height="1440px"
                  selectedDate={currentDataSelected}
                  currentView="TimelineMonth"
                  resourceHeaderTemplate={(props: any) => (
                    <button
                      type="button"
                      className="template-wrap group relative w-full cursor-pointer overflow-hidden rounded-xl border border-transparent bg-white p-3 text-left transition-all duration-300 hover:border-primary-100 hover:bg-gradient-to-br hover:from-white hover:to-primary-50/30 "
                      onClick={() => {
                        const navigateTo: string =
                          PUBLIC_PATH +
                          (
                            APP_ROUTES.UNIT_TYPE_MODULE.CHILDREN as RouteConfig
                          ).EDIT.FULL_PATH.replace(':id', props.resourceData.unitTypeId);
                        window.open(navigateTo, '_blank')?.focus();
                      }}
                    >
                      <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                      <div className="relative flex flex-col items-center gap-4 md:flex-row">
                        {/* <img
                          className="relative h-14 w-14 rounded-sm object-cover shadow-md"
                          src={
                            getUnitTypeImage(props) ||
                            `${PUBLIC_PATH}/assets/pngs/unit-type-default.png`
                          }
                          alt={getUnitTypeName(props)}
                        /> */}
                        <div className="flex w-full min-w-0 flex-col items-center md:items-start">
                          <Tooltip
                            placement="top"
                            title={
                              <span className="text-base font-gilroy-medium">
                                {getUnitTypeName(props)}
                              </span>
                            }
                          >
                            <div className="max-w-[160px] cursor-help truncate text-center font-gilroy-bold text-lg font-bold text-neutral-900 antialiased md:text-left">
                              {getUnitTypeName(props)}
                            </div>
                          </Tooltip>
                          <Tooltip
                            title={
                              <span className="text-base font-gilroy-medium">
                                {getUnitTypeDescription(props)}
                              </span>
                            }
                          >
                            <div className="mt-1 max-w-[140px] truncate text-center text-base font-gilroy-medium font-medium text-neutral-500 md:text-left">
                              {getUnitTypeDescription(props)}
                            </div>
                          </Tooltip>
                        </div>
                      </div>
                    </button>
                  )}
                  eventSettings={{
                    dataSource: events,
                    template: ((props: any) => {
                      return (
                        <div
                          style={{
                            backgroundColor: props.blockedDate
                              ? 'black'
                              : CALENDAR_COLORS[Math.floor(Math.random() * CALENDAR_COLORS.length)]
                          }}
                          className="flex min-h-[40px] items-start justify-start text-white"
                        >
                          <div
                            onClick={() => {
                              const navigateTo = props.blockedDateId
                                ? PUBLIC_PATH +
                                  (APP_ROUTES.BLOCK_DATES_MODULE.CHILDREN as RouteConfig).LIST
                                    .FULL_PATH
                                : PUBLIC_PATH +
                                  (
                                    APP_ROUTES.RESERVATION_MODULE.CHILDREN as RouteConfig
                                  ).EDIT.FULL_PATH.replace(':id', props.reservationId);
                              window.open(navigateTo, '_blank')?.focus();
                            }}
                          >
                            {props.Subject}
                            {'  '}
                          </div>
                          <button
                            className="ml-2 text-primary-500 underline transition-colors hover:text-white"
                            onClick={() => {
                              const navigateTo = props.blockedDateId
                                ? PUBLIC_PATH +
                                  (APP_ROUTES.BLOCK_DATES_MODULE.CHILDREN as RouteConfig).LIST
                                    .FULL_PATH
                                : PUBLIC_PATH +
                                  (
                                    APP_ROUTES.RESERVATION_MODULE.CHILDREN as RouteConfig
                                  ).EDIT.FULL_PATH.replace(':id', props.reservationId);
                              window.open(navigateTo, '_blank')?.focus();
                            }}
                          >
                            #{props.reservationId || props.blockedDateId}
                          </button>
                        </div>
                      );
                    }) as any
                  }}
                  group={{ enableCompactView: false, resources: ['Units'] }}
                >
                  <ResourcesDirective>
                    <ResourceDirective
                      field="id"
                      title="Room name"
                      name="Units"
                      allowMultiple={true}
                      dataSource={sortedUnitsData}
                      textField="name"
                      idField="id"
                      colorField="color"
                    />
                  </ResourcesDirective>
                  <ViewsDirective>
                    {/* <ViewDirective option="Day" /> */}
                    {/* <ViewDirective option="TimelineDay" /> */}
                    <ViewDirective option="TimelineMonth" />
                  </ViewsDirective>
                  <Inject services={[TimelineViews, TimelineMonth, Resize]} />
                </ScheduleComponent>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <LoadingData />
        </div>
      )}
    </div>
  );
};
